import React, { useEffect, useState } from 'react';
import { 
  Table, Button, Modal, Form, Input, InputNumber, Switch, Space, message, 
  Card, Divider, Select, DatePicker, Tag, Statistic, Row, Col, Typography, Tabs,
  Descriptions, List, Tooltip, Popconfirm
} from 'antd';
import { 
  PlusOutlined, EyeOutlined, ReloadOutlined, MoneyCollectOutlined, 
  TransactionOutlined, FilterOutlined, ClearOutlined, BankOutlined, UnorderedListOutlined,
  EditOutlined, LinkOutlined, ApartmentOutlined
} from '@ant-design/icons';
import {
  fetchFunds, createFund, updateFund, getFundById, getFundTransactions, getFundBalance,
  allocateProfitToFund, manualFundTransaction, allocateLossToFund, transferBetweenFunds,
  getAccounts, linkFundToAccount, loadFund
} from '../api';
import {
  fetchDispatches, getSmelterInvoice, recordPayment as recordSmelterPayment,
} from '../../smelter/api';
import DenominationCountForm from '../../treasury/components/DenominationCountForm';
import { DENOMINATION_NOTES } from '../../treasury/constants/treasuryConstants';
import dayjs from 'dayjs';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Purpose tags scope a fund to a business flow (mirrors CompanyFund.FUND_PURPOSES on the backend).
const FUND_PURPOSE_OPTIONS = [
  { value: 'GOLD_PURCHASE', label: 'Gold Purchase (walk-in & pledge)' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'CASH', label: 'Cash' },
  { value: 'GENERAL', label: 'General' },
];
const PURPOSE_LABEL = Object.fromEntries(FUND_PURPOSE_OPTIONS.map((p) => [p.value, p.label]));

const denominationTotal = (counts = {}) =>
  DENOMINATION_NOTES.reduce((sum, note) => sum + (counts[note.key] || 0) * note.value, 0);

const FundsPage = ({ onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [manualTxnOpen, setManualTxnOpen] = useState(false);
  const [profitAllocOpen, setProfitAllocOpen] = useState(false);
  const [lossAllocOpen, setLossAllocOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const [txFilters, setTxFilters] = useState({ source: null, type: null, dateRange: null });
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [manualTxnForm] = Form.useForm();
  const [profitForm] = Form.useForm();
  const [lossForm] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [linkForm] = Form.useForm();

  // Edit & View state
  const [editOpen, setEditOpen] = useState(false);
  const [editFund, setEditFund] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewFund, setViewFund] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Link CoA state
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkFund, setLinkFund] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  // Load Funds state
  const [loadOpen, setLoadOpen] = useState(false);
  const [loadFundRecord, setLoadFundRecord] = useState(null);
  const [loadLoading, setLoadLoading] = useState(false);
  const [loadForm] = Form.useForm();
  const [loadDenoms, setLoadDenoms] = useState({});
  const [createIsCashFund, setCreateIsCashFund] = useState(false);

  // Smelter-payment shortcut state (Load Funds on a smelter-linked fund)
  const [smelterDispatches, setSmelterDispatches] = useState([]);
  const [dispatchListLoading, setDispatchListLoading] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [dispatchInvoice, setDispatchInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Asset (Cash/Bank) accounts for the linked-account dropdown in create/edit
  const [assetAccounts, setAssetAccounts] = useState([]);

  // All transactions state
  const [allTransactions, setAllTransactions] = useState([]);
  const [allTxLoading, setAllTxLoading] = useState(false);
  const [allTxFilters, setAllTxFilters] = useState({ 
    fundId: null, source: null, type: null, dateRange: null 
  });
  
  const fmt = (v) => new Intl.NumberFormat('en-IN', { 
    style: 'currency', currency: 'INR', maximumFractionDigits: 0 
  }).format(v || 0);
  
  const TRANSACTION_SOURCES = [
    'CONTRIBUTION', 'EXPENSE', 'SMELTER_REVENUE', 'SETTLEMENT',
    'PROFIT_ALLOCATION', 'LOSS_ALLOCATION', 'TRANSFER_IN', 'TRANSFER_OUT',
    'CONTRIBUTION_REVERSAL', 'EXPENSE_REVERSAL',
    'MANUAL_DEPOSIT', 'MANUAL_WITHDRAWAL', 'FUND_LOAD', 'BRANCH_FUNDING'
  ];
  
  const SOURCE_COLORS = {
    CONTRIBUTION: 'blue', 
    EXPENSE: 'red',
    SMELTER_REVENUE: 'green',
    SETTLEMENT: 'geekblue',
    PROFIT_ALLOCATION: 'purple',
    LOSS_ALLOCATION: 'volcano',
    TRANSFER_IN: 'cyan',
    TRANSFER_OUT: 'orange',
    CONTRIBUTION_REVERSAL: 'magenta',
    EXPENSE_REVERSAL: 'lime',
    MANUAL_DEPOSIT: 'cyan',
    MANUAL_WITHDRAWAL: 'orange',
    FUND_LOAD: 'green',
    BRANCH_FUNDING: 'purple'
  };

  useEffect(() => { load(); loadAssetAccounts(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchFunds();
      setData(res.data || []);
    } catch (e) { message.error('Failed to load funds'); }
    finally { setLoading(false); }
  };

  // Cash/Bank (ASSET) accounts a fund can be linked to — drives the GL account the fund posts against.
  const loadAssetAccounts = async () => {
    try {
      const res = await getAccounts({ isActive: true, type: 'ASSET' });
      setAssetAccounts(res.data || []);
    } catch (e) { /* non-fatal — dropdown just stays empty */ }
  };

  const openLoad = (fund) => {
    setLoadFundRecord(fund);
    loadForm.resetFields();
    setLoadDenoms({});
    setSelectedDispatch(null);
    setDispatchInvoice(null);
    setLoadOpen(true);
    // Smelter-linked fund: the load is a shortcut for Record Smelter Payment,
    // so offer the dispatches that are awaiting payment.
    if (fund.isSmelterPaymentFund) {
      setDispatchListLoading(true);
      fetchDispatches({ status: 'RECEIPT_CONFIRMED', limit: 100 })
        .then((res) => setSmelterDispatches(res.data || []))
        .catch(() => message.error('Failed to load smelter dispatches'))
        .finally(() => setDispatchListLoading(false));
    }
  };

  // Selecting a dispatch pulls its tax invoice — the smelter pays the GST-inclusive total.
  const onSelectDispatch = async (dispatchId) => {
    const d = smelterDispatches.find((x) => x._id === dispatchId) || null;
    setSelectedDispatch(d);
    setDispatchInvoice(null);
    if (!d) return;
    setInvoiceLoading(true);
    try {
      const res = await getSmelterInvoice(dispatchId);
      setDispatchInvoice(res.data);
    } catch (e) {
      setDispatchInvoice(null); // 404 = bill not generated yet
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Smelter shortcut: runs the real Record Smelter Payment flow — marks the dispatch
  // PAYMENT_RECEIVED (stock out), computes profit, posts journals and credits this fund.
  const handleSmelterShortcut = async (values) => {
    if (!selectedDispatch) { message.error('Select a dispatch'); return; }
    if (!dispatchInvoice) {
      message.error('No tax invoice for this dispatch — open the dispatch and Generate Bill first');
      return;
    }
    setLoadLoading(true);
    try {
      await recordSmelterPayment(selectedDispatch._id, {
        paymentSplits: [{ mode: 'BANK_TRANSFER', amount: dispatchInvoice.total, reference: values.bankReference }],
        paymentDate: values.valueDate ? values.valueDate.toISOString() : undefined,
      });
      message.success(`Smelter payment ${fmt(dispatchInvoice.total)} recorded into ${loadFundRecord.name}`);
      setLoadOpen(false);
      setLoadFundRecord(null);
      setSelectedDispatch(null);
      setDispatchInvoice(null);
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to record smelter payment');
    } finally {
      setLoadLoading(false);
    }
  };

  const handleLoadFund = async (values) => {
    if (loadFundRecord?.isSmelterPaymentFund) {
      await handleSmelterShortcut(values);
      return;
    }
    // Cash-drawer funds derive the amount from the denomination breakdown.
    const isCash = !!loadFundRecord?.isIBCTCashFund;
    const amount = isCash ? denominationTotal(loadDenoms) : values.amount;
    if (isCash && amount <= 0) {
      message.error('Enter a denomination breakdown (total must be greater than 0)');
      return;
    }

    setLoadLoading(true);
    try {
      const payload = {
        ...values,
        amount,
        valueDate: values.valueDate ? values.valueDate.format('YYYY-MM-DD') : undefined,
        ...(isCash ? { denominations: loadDenoms } : {}),
      };
      await loadFund(loadFundRecord._id, payload);
      message.success(`Loaded ${fmt(amount)} into ${loadFundRecord.name}`);
      setLoadOpen(false);
      setLoadFundRecord(null);
      setLoadDenoms({});
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Fund load failed');
    } finally {
      setLoadLoading(false);
    }
  };

  const loadAllTransactions = async () => {
    setAllTxLoading(true);
    try {
      // Fetch transactions from all funds
      let allTxns = [];
      for (const fund of data) {
        const res = await getFundTransactions(fund._id);
        const fundTxns = (res.data || []).map(tx => ({
          ...tx,
          fundName: fund.name,
          fundId: fund._id
        }));
        allTxns = [...allTxns, ...fundTxns];
      }

      // Apply filters
      let filteredTxns = [...allTxns];
      if (allTxFilters.fundId) {
        filteredTxns = filteredTxns.filter(tx => tx.fundId === allTxFilters.fundId);
      }
      if (allTxFilters.source) {
        filteredTxns = filteredTxns.filter(tx => tx.source === allTxFilters.source);
      }
      if (allTxFilters.type) {
        filteredTxns = filteredTxns.filter(tx => tx.type === allTxFilters.type);
      }
      if (allTxFilters.dateRange && allTxFilters.dateRange.length === 2) {
        const [start, end] = allTxFilters.dateRange;
        filteredTxns = filteredTxns.filter(tx => {
          const txDate = dayjs(tx.createdAt);
          return txDate.isAfter(start.startOf('day')) && txDate.isBefore(end.endOf('day'));
        });
      }

      // Sort by date descending
      filteredTxns.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setAllTransactions(filteredTxns);
    } catch (e) {
      message.error('Failed to load transactions');
    } finally {
      setAllTxLoading(false);
    }
  };

  const clearAllFilters = () => {
    setAllTxFilters({ fundId: null, source: null, type: null, dateRange: null });
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      await createFund(values);
      message.success('Fund created');
      form.resetFields();
      setCreateOpen(false);
      load();
    } catch (e) { 
      message.error(e.response?.data?.message || 'Failed to create fund'); 
    } finally { setCreating(false); }
  };
  
  const handleManualTransaction = async (values) => {
    try {
      const { fundId, type, amount, description } = values;
      const backendType = type === 'CREDIT' ? 'DEPOSIT' : 'WITHDRAWAL';
      await manualFundTransaction(fundId, { amount, type: backendType, narration: description });
      message.success(`${type === 'CREDIT' ? 'Deposit' : 'Withdrawal'} completed`);
      manualTxnForm.resetFields();
      setManualTxnOpen(false);
      load();
      if (selectedFund && txModalOpen) {
        loadTransactions(selectedFund._id);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Transaction failed');
    }
  };
  
  const handleProfitAllocation = async (values) => {
    try {
      const { fundId, amount, description } = values;
      await allocateProfitToFund(fundId, { amount, remarks: description });
      message.success('Profit allocated to fund');
      profitForm.resetFields();
      setProfitAllocOpen(false);
      load();
      if (selectedFund && txModalOpen) {
        loadTransactions(selectedFund._id);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Allocation failed');
    }
  };

  const handleLossAllocation = async (values) => {
    try {
      const { fundId, amount, description } = values;
      await allocateLossToFund(fundId, { amount, remarks: description });
      message.success('Loss allocated (fund debited)');
      lossForm.resetFields();
      setLossAllocOpen(false);
      load();
      if (selectedFund && txModalOpen) {
        loadTransactions(selectedFund._id);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Loss allocation failed');
    }
  };

  const handleTransfer = async (values) => {
    try {
      await transferBetweenFunds(values);
      message.success('Funds transferred successfully');
      transferForm.resetFields();
      setTransferOpen(false);
      load();
      if (selectedFund && txModalOpen) {
        loadTransactions(selectedFund._id);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.message || e.message || 'Transfer failed';
      message.error(errorMsg);
    }
  };

  const openTransactions = async (fund) => {
    setSelectedFund(fund);
    setTxModalOpen(true);
    await loadTransactions(fund._id);
  };
  
  const loadTransactions = async (fundId) => {
    setTxLoading(true);
    try {
      const res = await getFundTransactions(fundId);
      let txnData = res.data || [];
      
      // Apply filters
      if (txFilters.source) {
        txnData = txnData.filter(tx => tx.source === txFilters.source);
      }
      if (txFilters.type) {
        txnData = txnData.filter(tx => tx.type === txFilters.type);
      }
      if (txFilters.dateRange && txFilters.dateRange.length === 2) {
        const [start, end] = txFilters.dateRange;
        txnData = txnData.filter(tx => {
          const txDate = dayjs(tx.createdAt);
          return txDate.isAfter(start.startOf('day')) && txDate.isBefore(end.endOf('day'));
        });
      }
      
      setTransactions(txnData);
    } catch (e) {
      message.error('Failed to load transactions');
    } finally { setTxLoading(false); }
  };
  
  const clearFilters = () => {
    setTxFilters({ source: null, type: null, dateRange: null });
    if (selectedFund) loadTransactions(selectedFund._id);
  };

  // ── Edit Fund ──
  const openEdit = (fund) => {
    setEditFund(fund);
    editForm.setFieldsValue({
      name: fund.name,
      description: fund.description || '',
      currency: fund.currency || 'INR',
      purpose: fund.purpose || 'GENERAL',
      linkedAccountCode: fund.linkedAccountCode || undefined,
      isCentralFund: fund.isCentralFund || false,
      isIBCTCashFund: fund.isIBCTCashFund || false,
      isSmelterPaymentFund: fund.isSmelterPaymentFund || false,
    });
    setEditOpen(true);
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      await updateFund(editFund._id, values);
      message.success('Fund updated');
      setEditOpen(false);
      setEditFund(null);
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to update fund');
    } finally {
      setEditLoading(false);
    }
  };

  // ── View Fund Detail ──
  const openView = async (fund) => {
    setViewLoading(true);
    setViewOpen(true);
    try {
      const res = await getFundById(fund._id);
      setViewFund(res.data || res);
    } catch (e) {
      message.error('Failed to load fund details');
      setViewFund(fund);
    } finally {
      setViewLoading(false);
    }
  };

  // ── Link CoA to Fund ──
  const openLinkCoA = async (fund) => {
    setLinkFund(fund);
    setLinkOpen(true);
    linkForm.resetFields();
    try {
      const res = await getAccounts({ isActive: true });
      setAllAccounts(res.data || []);
    } catch (e) {
      message.error('Failed to load accounts');
    }
  };

  const handleLinkAccount = async (values) => {
    setLinkLoading(true);
    try {
      await linkFundToAccount(values.accountId, linkFund._id);
      message.success('Account linked to fund');
      setLinkOpen(false);
      setLinkFund(null);
      linkForm.resetFields();
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to link account');
    } finally {
      setLinkLoading(false);
    }
  };

  const refreshBalance = async (fund) => {
    try {
      const res = await getFundBalance(fund._id);
      message.success(`${res.data.name}: ${fmt(res.data.balance)}`);
      load();
    } catch {
      message.error('Failed to fetch balance');
    }
  };
  
  // Watch for filter changes
  useEffect(() => {
    if (selectedFund && txModalOpen) {
      loadTransactions(selectedFund._id);
    }
  }, [txFilters]);

  // Load all transactions when data changes or filters change
  useEffect(() => {
    if (data.length > 0) {
      loadAllTransactions();
    }
  }, [data, allTxFilters]);

  const columns = [
    { title: 'Fund ID', dataIndex: 'fundId', key: 'fundId', width: 100 },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name, r) => (
        <Space size={4} wrap>
          <Text>{name}</Text>
          {r.isCentralFund && <Tag color="gold">Central</Tag>}
          {r.isIBCTCashFund && <Tag color="volcano">IBCT Cash</Tag>}
          {r.isSmelterPaymentFund && <Tag color="green">Smelter Sales</Tag>}
        </Space>
      ),
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      width: 150,
      render: (p) => <Tag>{PURPOSE_LABEL[p] || p || 'General'}</Tag>,
    },
    {
      title: 'GL Account',
      dataIndex: 'linkedAccountCode',
      key: 'linkedAccountCode',
      width: 100,
      render: (c) => (c ? <Text code>{c}</Text> : <Text type="secondary">—</Text>),
    },
    { 
      title: 'Balance', 
      dataIndex: 'balance', 
      key: 'balance', 
      width: 120,
      render: (v) => (
        <Text strong style={{ color: v >= 0 ? '#3f8600' : '#cf1322' }}>
          {fmt(v)}
        </Text>
      )
    },
    { title: 'Actions', key: 'actions', width: 280, render: (_, r) => (
      <Space size="small" wrap>
        <Tooltip title="View Details">
          <Button size="small" icon={<EyeOutlined />} onClick={() => openView(r)} />
        </Tooltip>
        <Tooltip title="Edit Fund">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
        </Tooltip>
        <Tooltip title="Link Chart of Account">
          <Button size="small" icon={<LinkOutlined />} onClick={() => openLinkCoA(r)} />
        </Tooltip>
        <Tooltip title="Load Funds (bank credit / capital)">
          <Button size="small" type="primary" ghost icon={<MoneyCollectOutlined />} onClick={() => openLoad(r)}>Load</Button>
        </Tooltip>
        <Button size="small" icon={<TransactionOutlined />} onClick={() => openTransactions(r)}>Txns</Button>
        <Tooltip title="Refresh Balance">
          <Button size="small" icon={<ReloadOutlined />} onClick={() => refreshBalance(r)} />
        </Tooltip>
      </Space>
    )},
  ];
  
  const totalBalance = data.reduce((sum, fund) => sum + fund.balance, 0);

  const allTransactionsColumns = [
    { 
      title: 'Fund', 
      dataIndex: 'fundName', 
      key: 'fundName', 
      width: 140,
      render: (name, record) => (
        <Tag color="blue">{name}</Tag>
      )
    },
    { 
      title: 'Type', 
      dataIndex: 'type', 
      key: 'type', 
      width: 80,
      render: (type) => (
        <Tag color={type === 'CREDIT' ? 'green' : 'red'}>
          {type === 'CREDIT' ? '+' : '-'} {type}
        </Tag>
      )
    },
    { 
      title: 'Source', 
      dataIndex: 'source', 
      key: 'source', 
      width: 140,
      render: (source) => (
        <Tag color={SOURCE_COLORS[source] || 'default'}>
          {source?.replace(/_/g, ' ') || 'N/A'}
        </Tag>
      )
    },
    { 
      title: 'Amount', 
      dataIndex: 'amount', 
      key: 'amount', 
      width: 120,
      render: (amount, record) => (
        <Text strong style={{ color: record.type === 'CREDIT' ? '#3f8600' : '#cf1322' }}>
          {record.type === 'CREDIT' ? '+' : '-'}{fmt(amount)}
        </Text>
      )
    },
    { 
      title: 'Balance After', 
      dataIndex: 'balanceAfter', 
      key: 'balanceAfter', 
      width: 130,
      render: (balance) => (
        <Text style={{ color: balance >= 0 ? '#3f8600' : '#cf1322' }}>
          {fmt(balance)}
        </Text>
      )
    },
    { 
      title: 'Description', 
      dataIndex: 'narration', 
      key: 'narration',
      ellipsis: true
    },
    { 
      title: 'Date', 
      dataIndex: 'createdAt', 
      key: 'createdAt', 
      width: 160,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
    },
  ];

  const fundsManagementTab = (
    <div>
      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic 
              title="Total Fund Balance" 
              value={fmt(totalBalance)}
              valueStyle={{ color: totalBalance >= 0 ? '#3f8600' : '#cf1322' }}
              prefix={<MoneyCollectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic title="Active Funds" value={data.length} prefix={<BankOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Space wrap>
            <Button 
              icon={<TransactionOutlined />} 
              onClick={() => setManualTxnOpen(true)}
            >
              Manual Txn
            </Button>
            <Button 
              icon={<MoneyCollectOutlined />} 
              onClick={() => setProfitAllocOpen(true)}
            >
              Allocate Profit
            </Button>
            <Button 
              danger
              onClick={() => setLossAllocOpen(true)}
            >
              Allocate Loss
            </Button>
            <Button 
              type="dashed"
              icon={<TransactionOutlined />} 
              onClick={() => setTransferOpen(true)}
            >
              Transfer
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Funds Table */}
      <Card 
        size="small" 
        title="Company Funds" 
        extra={
          <Button icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateIsCashFund(false); setCreateOpen(true); }}>
            Create Fund
          </Button>
        }
      >
        <DataTable persistId="accounting.funds.manage" 
          dataSource={data} 
          rowKey={(r) => r._id} 
          columns={columns} 
          loading={loading} 
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );

  const allTransactionsTab = (
    <div>
      {/* Transaction Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="Total Credits" 
              value={fmt(allTransactions.filter(tx => tx.type === 'CREDIT').reduce((sum, tx) => sum + tx.amount, 0))}
              valueStyle={{ color: '#3f8600', fontSize: 18 }}
              prefix={<MoneyCollectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="Total Debits" 
              value={fmt(allTransactions.filter(tx => tx.type === 'DEBIT').reduce((sum, tx) => sum + tx.amount, 0))}
              valueStyle={{ color: '#cf1322', fontSize: 18 }}
              prefix={<MoneyCollectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="Total Transactions" 
              value={allTransactions.length}
              valueStyle={{ fontSize: 18 }}
              prefix={<UnorderedListOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadAllTransactions}
              style={{ width: '100%' }}
            >
              Refresh All
            </Button>
          </Card>
        </Col>
      </Row>

      {/* Transaction Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={6}>
            <Select
              placeholder="All Funds"
              allowClear
              style={{ width: '100%' }}
              value={allTxFilters.fundId}
              onChange={(value) => setAllTxFilters(prev => ({ ...prev, fundId: value }))}
            >
              {data.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="All Sources"
              allowClear
              style={{ width: '100%' }}
              value={allTxFilters.source}
              onChange={(value) => setAllTxFilters(prev => ({ ...prev, source: value }))}
            >
              {TRANSACTION_SOURCES.map(source => (
                <Select.Option key={source} value={source}>
                  {source.replace(/_/g, ' ')}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="All Types"
              allowClear
              style={{ width: '100%' }}
              value={allTxFilters.type}
              onChange={(value) => setAllTxFilters(prev => ({ ...prev, type: value }))}
            >
              <Select.Option value="CREDIT">Credit</Select.Option>
              <Select.Option value="DEBIT">Debit</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={allTxFilters.dateRange}
              onChange={(dates) => setAllTxFilters(prev => ({ ...prev, dateRange: dates }))}
            />
          </Col>
          <Col xs={24} sm={2}>
            <Button 
              icon={<ClearOutlined />} 
              onClick={clearAllFilters}
              title="Clear filters"
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* All Transactions Table */}
      <Card size="small" title="All Fund Transactions">
        <DataTable persistId="accounting.funds.all-transactions" 
          dataSource={allTransactions} 
          rowKey={(r) => `${r.fundId}-${r._id}`} 
          loading={allTxLoading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} transactions` }}
          size="small"
          scroll={{ x: 1000 }}
          columns={allTransactionsColumns}
        />
      </Card>
    </div>
  );

  return (
    <div>
      <Tabs
        defaultActiveKey="funds"
        items={[
          {
            key: 'funds',
            label: (
              <span>
                <BankOutlined />
                Manage Funds
              </span>
            ),
            children: fundsManagementTab,
          },
          {
            key: 'transactions',
            label: (
              <span>
                <UnorderedListOutlined />
                All Transactions
              </span>
            ),
            children: allTransactionsTab,
          },
        ]}
      />
      
      {/* All Modals */}

      {/* Create Fund Modal */}
      <Modal
        title="Create New Fund"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={creating}
        width={600}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleCreate}
          initialValues={{ currency: 'INR', balance: 0, purpose: 'GENERAL' }}
        >
          <Form.Item name="name" label="Fund Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Main Operating Fund" />
          </Form.Item>
          <Form.Item name="purpose" label="Purpose" rules={[{ required: true }]}
            tooltip="Scopes the fund. Walk-in/pledge payouts only list Gold Purchase funds; expenses only list Operations funds."
          >
            <Select options={FUND_PURPOSE_OPTIONS} placeholder="Select purpose" />
          </Form.Item>
          <Form.Item name="linkedAccountCode" label="Linked GL Account (Cash / Bank)"
            tooltip="The Chart-of-Accounts asset account this fund posts against. Loads credit Owner's Equity and debit this account."
            rules={[{ required: true, message: 'Pick the Cash/Bank account this fund represents' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select the bank/cash account"
              options={assetAccounts.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` }))}
            />
          </Form.Item>
          {!createIsCashFund && (
            <Form.Item name="balance" label="Opening Balance"
              tooltip="Posts a journaled load: Dr the linked account / Cr Owner's Equity (3000). Leave 0 and use Load Funds later if preferred."
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
              />
            </Form.Item>
          )}
          <Form.Item name="currency" label="Currency">
            <Input disabled />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Brief description of fund purpose" />
          </Form.Item>
          <Form.Item name="isCentralFund" label="Link to Central Fund (HO Bank)" valuePropName="checked"
            tooltip="Marks this fund as the mirror of the HO treasury bank ledger. Loads here also post a Bank Credit to the Central Fund. Only one fund can be the Central Fund."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="isIBCTCashFund" label="IBCT Cash Fund (branch-funding cash drawer)" valuePropName="checked"
            tooltip="Marks this fund as the physical-cash pool that Fund Branch draws from. Tracks a denomination inventory. Fund it via Load Funds with a note breakdown (no opening balance). Only one fund can be the IBCT Cash Fund."
          >
            <Switch onChange={setCreateIsCashFund} />
          </Form.Item>
          <Form.Item name="isSmelterPaymentFund" label="Link to Record Smelter Payment (sales fund)" valuePropName="checked"
            tooltip="Record Smelter Payment credits the full GST-inclusive receipt (UTR amount) into this fund as a SMELTER REVENUE transaction, and Load Funds becomes a shortcut to record a smelter payment. Only one fund can be linked."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Manual Transaction Modal */}
      <Modal
        title="Manual Fund Transaction"
        open={manualTxnOpen}
        onCancel={() => setManualTxnOpen(false)}
        onOk={() => manualTxnForm.submit()}
        width={600}
      >
        <Form 
          form={manualTxnForm} 
          layout="vertical" 
          onFinish={handleManualTransaction}
        >
          <Form.Item name="fundId" label="Select Fund" rules={[{ required: true }]}>
            <Select placeholder="Choose fund">
              {data.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name} ({fmt(fund.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="type" label="Transaction Type" rules={[{ required: true }]}>
            <Select placeholder="Select type">
              <Select.Option value="CREDIT">Deposit (Credit)</Select.Option>
              <Select.Option value="DEBIT">Withdrawal (Debit)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <InputNumber 
              style={{ width: '100%' }} 
              min={0.01}
              formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Reason for this transaction" />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Profit Allocation Modal */}
      <Modal
        title="Allocate Profit to Fund"
        open={profitAllocOpen}
        onCancel={() => setProfitAllocOpen(false)}
        onOk={() => profitForm.submit()}
        width={600}
      >
        <Form 
          form={profitForm} 
          layout="vertical" 
          onFinish={handleProfitAllocation}
        >
          <Form.Item name="fundId" label="Target Fund" rules={[{ required: true }]}>
            <Select placeholder="Choose fund to allocate profit">
              {data.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name} ({fmt(fund.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Profit Amount" rules={[{ required: true }]}>
            <InputNumber 
              style={{ width: '100%' }} 
              min={0.01}
              formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea 
              rows={3} 
              placeholder="Source of profit (e.g., Q4 2025 net profit allocation)" 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Transactions Modal */}
      <Modal 
        title={`Fund Transactions — ${selectedFund?.name || ''}`} 
        open={txModalOpen} 
        onCancel={() => setTxModalOpen(false)} 
        footer={null} 
        width={1000}
      >
        {/* Transaction Filters */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8}>
              <Select
                placeholder="Filter by source"
                allowClear
                style={{ width: '100%' }}
                value={txFilters.source}
                onChange={(value) => setTxFilters(prev => ({ ...prev, source: value }))}
              >
                {TRANSACTION_SOURCES.map(source => (
                  <Select.Option key={source} value={source}>
                    {source.replace(/_/g, ' ')}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={6}>
              <Select
                placeholder="Filter by type"
                allowClear
                style={{ width: '100%' }}
                value={txFilters.type}
                onChange={(value) => setTxFilters(prev => ({ ...prev, type: value }))}
              >
                <Select.Option value="CREDIT">Credit</Select.Option>
                <Select.Option value="DEBIT">Debit</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <RangePicker
                style={{ width: '100%' }}
                value={txFilters.dateRange}
                onChange={(dates) => setTxFilters(prev => ({ ...prev, dateRange: dates }))}
              />
            </Col>
            <Col xs={24} sm={2}>
              <Button 
                icon={<ClearOutlined />} 
                onClick={clearFilters}
                title="Clear filters"
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card>
        
        {/* Summary Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="Total Credits" 
                value={fmt(transactions.filter(tx => tx.type === 'CREDIT').reduce((sum, tx) => sum + tx.amount, 0))}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="Total Debits" 
                value={fmt(transactions.filter(tx => tx.type === 'DEBIT').reduce((sum, tx) => sum + tx.amount, 0))}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="Current Balance" 
                value={fmt(selectedFund?.balance || 0)}
                valueStyle={{ color: (selectedFund?.balance || 0) >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
        
        {/* Transactions Table */}
        <DataTable persistId="accounting.funds.fund-transactions" 
          dataSource={transactions} 
          rowKey={(r) => r._id} 
          loading={txLoading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          size="small"
          columns={[
            { 
              title: 'Type', 
              dataIndex: 'type', 
              key: 'type', 
              width: 80,
              render: (type) => (
                <Tag color={type === 'CREDIT' ? 'green' : 'red'}>
                  {type === 'CREDIT' ? '+' : '-'} {type}
                </Tag>
              )
            },
            { 
              title: 'Source', 
              dataIndex: 'source', 
              key: 'source', 
              width: 140,
              render: (source) => (
                <Tag color={SOURCE_COLORS[source] || 'default'}>
                  {source?.replace(/_/g, ' ') || 'N/A'}
                </Tag>
              )
            },
            { 
              title: 'Amount', 
              dataIndex: 'amount', 
              key: 'amount', 
              width: 120,
              render: (amount, record) => (
                <Text strong style={{ color: record.type === 'CREDIT' ? '#3f8600' : '#cf1322' }}>
                  {record.type === 'CREDIT' ? '+' : '-'}{fmt(amount)}
                </Text>
              )
            },
            { 
              title: 'Balance After', 
              dataIndex: 'balanceAfter', 
              key: 'balanceAfter', 
              width: 130,
              render: (balance) => (
                <Text style={{ color: balance >= 0 ? '#3f8600' : '#cf1322' }}>
                  {fmt(balance)}
                </Text>
              )
            },
            { 
              title: 'Description', 
              dataIndex: 'narration', 
              key: 'narration',
              ellipsis: true
            },
            { 
              title: 'Date', 
              dataIndex: 'createdAt', 
              key: 'createdAt', 
              width: 160,
              render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
            },
          ]} 
        />
      </Modal>

      {/* Loss Allocation Modal */}
      <Modal
        title="Allocate Loss to Fund"
        open={lossAllocOpen}
        onCancel={() => setLossAllocOpen(false)}
        onOk={() => lossForm.submit()}
        width={600}
      >
        <Form form={lossForm} layout="vertical" onFinish={handleLossAllocation}>
          <Form.Item name="fundId" label="Target Fund" rules={[{ required: true }]}>
            <Select placeholder="Choose fund to debit loss from">
              {data.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name} ({fmt(fund.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Loss Amount" rules={[{ required: true }]}>
            <InputNumber 
              style={{ width: '100%' }} 
              min={0.01}
              formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item name="description" label="Remarks">
            <Input.TextArea rows={3} placeholder="Reason for loss allocation" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Fund Transfer Modal */}
      <Modal
        title="Transfer Between Funds"
        open={transferOpen}
        onCancel={() => setTransferOpen(false)}
        onOk={() => transferForm.submit()}
        width={600}
      >
        <Form form={transferForm} layout="vertical" onFinish={handleTransfer}>
          <Form.Item name="fromFundId" label="From Fund" rules={[{ required: true }]}>
            <Select placeholder="Source fund">
              {data.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name} ({fmt(fund.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="toFundId" label="To Fund" rules={[{ required: true }]}>
            <Select placeholder="Destination fund">
              {data.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name} ({fmt(fund.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Transfer Amount" rules={[{ required: true }]}>
            <InputNumber 
              style={{ width: '100%' }} 
              min={0.01}
              formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item name="voucherRef" label="Voucher / Reference"
            tooltip="e.g. cash-withdrawal slip number when moving a bank fund to a cash fund. Recorded on both fund transactions and the journal."
          >
            <Input placeholder="Optional voucher / withdrawal slip reference" />
          </Form.Item>
          <Form.Item name="narration" label="Narration">
            <Input.TextArea rows={2} placeholder="Reason for transfer" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Load Funds Modal */}
      <Modal
        title={loadFundRecord?.isSmelterPaymentFund
          ? `Record Smelter Payment — ${loadFundRecord?.name || ''}`
          : `Load Funds — ${loadFundRecord?.name || ''}`}
        open={loadOpen}
        onCancel={() => { setLoadOpen(false); setLoadFundRecord(null); setSelectedDispatch(null); setDispatchInvoice(null); }}
        onOk={() => loadForm.submit()}
        confirmLoading={loadLoading}
        okText={loadFundRecord?.isSmelterPaymentFund ? 'Record Payment' : 'Load Funds'}
        okButtonProps={loadFundRecord?.isSmelterPaymentFund ? { disabled: !selectedDispatch || !dispatchInvoice } : {}}
        width={560}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {loadFundRecord?.isSmelterPaymentFund
            ? 'Shortcut for Record Smelter Payment: marks the dispatch PAYMENT RECEIVED (stock goes out), computes profit, posts the journals and credits this fund with the GST-inclusive receipt.'
            : <>Posts a journal entry: <Text code>Dr {loadFundRecord?.linkedAccountCode || '1100'}</Text> (this fund's account) / <Text code>Cr 3000</Text> Owner's Equity.
              {loadFundRecord?.isCentralFund ? ' Also records a Bank Credit on the Central Fund ledger.' : ''}
              {loadFundRecord?.isIBCTCashFund ? ' Cash drawer: the amount comes from the note breakdown below and updates the denomination inventory.' : ''}</>}
        </Text>
        <Form form={loadForm} layout="vertical" onFinish={handleLoadFund}>
          {loadFundRecord?.isSmelterPaymentFund ? (
            <>
              <Form.Item name="dispatchId" label="Smelter Dispatch (awaiting payment)"
                rules={[{ required: true, message: 'Select a dispatch' }]}
              >
                <Select
                  showSearch
                  loading={dispatchListLoading}
                  optionFilterProp="label"
                  placeholder="Select a RECEIPT CONFIRMED dispatch"
                  onChange={onSelectDispatch}
                  options={smelterDispatches.map((d) => ({
                    value: d._id,
                    label: `${d.dispatchId} — ${d.smelterName || d.smelterId?.name || ''} (${fmt(d.totalAmountFromSmelter)})`,
                  }))}
                />
              </Form.Item>
              {selectedDispatch && (
                <Descriptions bordered column={1} size="small" style={{ marginBottom: 12 }}>
                  <Descriptions.Item label="Inventory Batch">
                    {selectedDispatch.inventoryBatchId?.batchId || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Smelter">
                    {selectedDispatch.smelterName || selectedDispatch.smelterId?.name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Dispatch Date">
                    {selectedDispatch.dispatchDate ? dayjs(selectedDispatch.dispatchDate).format('DD/MM/YYYY') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount Received from Smelter (incl. GST)">
                    {invoiceLoading ? 'Loading invoice…' : dispatchInvoice
                      ? <Text strong style={{ color: '#3f8600' }}>{fmt(dispatchInvoice.total)} <Text type="secondary">(Invoice {dispatchInvoice.invoiceNo})</Text></Text>
                      : <Text type="danger">No bill generated — open the dispatch and use Generate Bill first</Text>}
                  </Descriptions.Item>
                </Descriptions>
              )}
              <Form.Item name="bankReference" label="UTR / Bank Reference"
                rules={[{ required: true, message: 'Enter the UTR / bank reference' }]}
              >
                <Input placeholder="UTR / NEFT / RTGS ref" />
              </Form.Item>
              <Form.Item name="valueDate" label="Payment Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </>
          ) : loadFundRecord?.isIBCTCashFund ? (
            <Form.Item label="Denomination Breakdown (cash in)" required>
              <div style={{ padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                <DenominationCountForm counts={loadDenoms} onChange={setLoadDenoms} />
              </div>
              <Text strong style={{ display: 'block', marginTop: 8 }}>
                Total: {fmt(denominationTotal(loadDenoms))}
              </Text>
            </Form.Item>
          ) : (
            <Form.Item name="amount" label="Amount" rules={[{ required: true, type: 'number', min: 1, message: 'Enter a positive amount' }]}>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                step={1000}
                formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
              />
            </Form.Item>
          )}
          {!loadFundRecord?.isSmelterPaymentFund && (
            <>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="bankAccount" label="Bank Account">
                    <Input placeholder="Account number / name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="bankReference" label="Bank Reference / UTR">
                    <Input placeholder="UTR / NEFT / RTGS ref" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="valueDate" label="Value Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item name="narration" label="Narration">
                <Input.TextArea rows={2} placeholder="Optional description (source of funds)" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Edit Fund Modal */}
      <Modal
        title={`Edit Fund — ${editFund?.name || ''}`}
        open={editOpen}
        onCancel={() => { setEditOpen(false); setEditFund(null); }}
        onOk={() => editForm.submit()}
        confirmLoading={editLoading}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="name" label="Fund Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g., Main Operating Fund" />
          </Form.Item>
          <Form.Item name="purpose" label="Purpose" rules={[{ required: true }]}
            tooltip="Scopes the fund. Walk-in/pledge payouts only list Gold Purchase funds; expenses only list Operations funds."
          >
            <Select options={FUND_PURPOSE_OPTIONS} placeholder="Select purpose" />
          </Form.Item>
          <Form.Item name="linkedAccountCode" label="Linked GL Account (Cash / Bank)"
            tooltip="The Chart-of-Accounts asset account this fund posts against."
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Select the bank/cash account"
              options={assetAccounts.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Brief description of fund purpose" />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input disabled />
          </Form.Item>
          <Form.Item name="isCentralFund" label="Link to Central Fund (HO Bank)" valuePropName="checked"
            tooltip="Marks this fund as the mirror of the HO treasury bank ledger. Only one fund can be the Central Fund."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="isIBCTCashFund" label="IBCT Cash Fund (branch-funding cash drawer)" valuePropName="checked"
            tooltip="Marks this fund as the physical-cash pool that Fund Branch draws from, with a denomination inventory. Only one fund can be the IBCT Cash Fund."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="isSmelterPaymentFund" label="Link to Record Smelter Payment (sales fund)" valuePropName="checked"
            tooltip="Record Smelter Payment credits the full GST-inclusive receipt (UTR amount) into this fund as a SMELTER REVENUE transaction, and Load Funds becomes a shortcut to record a smelter payment. Only one fund can be linked."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Fund Detail Modal */}
      <Modal
        title={`Fund Details — ${viewFund?.name || ''}`}
        open={viewOpen}
        onCancel={() => { setViewOpen(false); setViewFund(null); }}
        footer={[
          <Button key="edit" icon={<EditOutlined />} onClick={() => { setViewOpen(false); if (viewFund) openEdit(viewFund); }}>
            Edit
          </Button>,
          <Button key="link" icon={<LinkOutlined />} onClick={() => { setViewOpen(false); if (viewFund) openLinkCoA(viewFund); }}>
            Link Account
          </Button>,
          <Button key="close" type="primary" onClick={() => { setViewOpen(false); setViewFund(null); }}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {viewLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : viewFund ? (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Fund ID">{viewFund.fundId}</Descriptions.Item>
              <Descriptions.Item label="Name">{viewFund.name}</Descriptions.Item>
              <Descriptions.Item label="Balance">
                <Text strong style={{ color: (viewFund.balance || 0) >= 0 ? '#3f8600' : '#cf1322' }}>
                  {fmt(viewFund.balance)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Currency">{viewFund.currency || 'INR'}</Descriptions.Item>
              <Descriptions.Item label="Purpose">
                <Tag>{PURPOSE_LABEL[viewFund.purpose] || viewFund.purpose || 'General'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Roles">
                <Space size={4} wrap>
                  {viewFund.isCentralFund && <Tag color="gold">Central (HO Bank)</Tag>}
                  {viewFund.isIBCTCashFund && <Tag color="volcano">IBCT Cash Drawer</Tag>}
                  {viewFund.isSmelterPaymentFund && <Tag color="green">Smelter Sales</Tag>}
                  {!viewFund.isCentralFund && !viewFund.isIBCTCashFund && !viewFund.isSmelterPaymentFund && <Text type="secondary">—</Text>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {viewFund.isActive !== false ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {viewFund.description || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {viewFund.createdAt ? dayjs(viewFund.createdAt).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {viewFund.updatedAt ? dayjs(viewFund.updatedAt).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
            </Descriptions>

            {/* Cash drawer — denomination inventory (IBCT cash funds) */}
            {viewFund.isIBCTCashFund && (
              <>
                <Divider orientation="left" style={{ marginTop: 24 }}>
                  <MoneyCollectOutlined /> Cash Drawer — Denomination Inventory
                </Divider>
                <DataTable persistId="accounting.funds.denominations" showToolbar={false}
                  size="small"
                  pagination={false}
                  rowKey="key"
                  dataSource={DENOMINATION_NOTES.map((note) => ({
                    key: note.key,
                    label: note.label,
                    count: viewFund.denominations?.[note.key] || 0,
                    value: (viewFund.denominations?.[note.key] || 0) * note.value,
                  }))}
                  columns={[
                    { title: 'Note', dataIndex: 'label', key: 'label' },
                    { title: 'Count', dataIndex: 'count', key: 'count' },
                    { title: 'Value', dataIndex: 'value', key: 'value', render: (v) => fmt(v) },
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2}>
                        <Text strong>{fmt(viewFund.denominations?.totalPhysicalCash || 0)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </>
            )}

            {/* Linked Chart of Accounts */}
            <Divider orientation="left" style={{ marginTop: 24 }}>
              <ApartmentOutlined /> Linked Chart of Accounts
            </Divider>
            {viewFund.linkedAccounts && viewFund.linkedAccounts.length > 0 ? (
              <List
                size="small"
                bordered
                dataSource={viewFund.linkedAccounts}
                renderItem={(acct) => (
                  <List.Item>
                    <Space>
                      <Tag color="blue">{acct.code}</Tag>
                      <Text>{acct.name}</Text>
                      <Tag>{acct.type}</Tag>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">No accounts linked to this fund. Use the "Link Account" button to connect a Chart of Account.</Text>
            )}
          </>
        ) : null}
      </Modal>

      {/* Link Chart of Account to Fund Modal */}
      <Modal
        title={`Link Account to Fund — ${linkFund?.name || ''}`}
        open={linkOpen}
        onCancel={() => { setLinkOpen(false); setLinkFund(null); }}
        onOk={() => linkForm.submit()}
        confirmLoading={linkLoading}
        width={600}
      >
        <Form form={linkForm} layout="vertical" onFinish={handleLinkAccount}>
          <Form.Item
            name="accountId"
            label="Select Account to Link"
            rules={[{ required: true, message: 'Please select an account' }]}
            extra="Link a Chart of Account to this fund. This enables the journal bridge to use fund-specific accounts for transfers."
          >
            <Select
              showSearch
              placeholder="Search and select an account"
              optionFilterProp="label"
              options={allAccounts.map(acct => ({
                value: acct._id,
                label: `${acct.code} — ${acct.name} (${acct.type})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FundsPage;
